import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Grid,
    Typography,
    Chip,
    IconButton,
    Tooltip,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper,
    Divider
} from '@mui/material';
import {
    Close as CloseIcon,
    PictureAsPdf as PictureAsPdfIcon,
    Description as DescriptionIcon
} from '@mui/icons-material';
import { getLegacyPdfUrl } from '../../../services/clinicPortalServices/legacyViewerService';

const PatientDetail = ({ patient, onClose }) => {
    if (!patient) return null;

    const {
        firstName,
        lastName,
        dob,
        mrn,
        sex,
        phone,
        address,
        externalSourceId,
        requisitions = [],
        insurance = {},
        diagnoses = [],
        medications = []
    } = patient;

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        const d = new Date(dateStr);
        return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString();
    };

    const renderDemographicField = (label, value) => (
        <Grid item xs={12} sm={6} md={4}>
            <Typography variant="caption" color="text.secondary" display="block">
                {label}
            </Typography>
            <Typography variant="body1">
                {value || 'N/A'}
            </Typography>
        </Grid>
    );

    return (
        <Dialog
            open={true}
            onClose={onClose}
            fullWidth
            maxWidth="lg"
            PaperProps={{ sx: { minHeight: '60vh' } }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DescriptionIcon color="primary" />
                    <Typography variant="h6" component="span">
                        {lastName}, {firstName}
                    </Typography>
                    {mrn && (
                        <Chip label={`MRN: ${mrn}`} size="small" variant="outlined" />
                    )}
                </Box>
                <IconButton onClick={onClose} size="small" aria-label="close">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {/* Demographics Section */}
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                    Demographics
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                    <Grid container spacing={2}>
                        {renderDemographicField('Name', `${firstName || ''} ${lastName || ''}`.trim())}
                        {renderDemographicField('Date of Birth', formatDate(dob))}
                        {renderDemographicField('MRN', mrn)}
                        {renderDemographicField('Sex', sex)}
                        {renderDemographicField('Phone', phone)}
                        {renderDemographicField('Address', address)}
                        {renderDemographicField('External Source ID', externalSourceId)}
                    </Grid>
                </Paper>

                {/* Insurance Section */}
                {(insurance.company1 || insurance.policy1 || insurance.group1) && (
                    <>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                            Insurance
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                            <Grid container spacing={2}>
                                {renderDemographicField('Company', insurance.company1)}
                                {renderDemographicField('Policy #', insurance.policy1)}
                                {renderDemographicField('Group #', insurance.group1)}
                            </Grid>
                        </Paper>
                    </>
                )}

                {/* Diagnoses */}
                {diagnoses.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                            Diagnoses
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {diagnoses.map((diagnosis, idx) => (
                                <Chip
                                    key={idx}
                                    label={diagnosis}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                />
                            ))}
                        </Box>
                    </Box>
                )}

                {/* Medications */}
                {medications.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                            Medications
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {medications.map((medication, idx) => (
                                <Chip
                                    key={idx}
                                    label={medication}
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                />
                            ))}
                        </Box>
                    </Box>
                )}

                <Divider sx={{ mb: 2 }} />

                {/* Requisitions Section */}
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                    Requisitions ({requisitions.length})
                </Typography>

                {requisitions.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No requisitions found for this patient.
                    </Typography>
                ) : (
                    <Paper variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Requisition Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Collection Date</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Report Date</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="center"># PDFs</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {requisitions.map((req, idx) => (
                                    <TableRow
                                        key={req.legacyReqId || idx}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                    >
                                        <TableCell>
                                            {req.requisitionName || 'Unnamed Requisition'}
                                        </TableCell>
                                        <TableCell>
                                            {formatDate(req.collectionDate)}
                                        </TableCell>
                                        <TableCell>
                                            {formatDate(req.reportDate)}
                                        </TableCell>
                                        <TableCell align="center">
                                            {(req.reportFiles || []).length}
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                {(req.reportFiles || []).map((file, fileIdx) => (
                                                    <Tooltip
                                                        key={fileIdx}
                                                        title={file.fileName || 'Download PDF'}
                                                    >
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            component="a"
                                                            href={getLegacyPdfUrl(file.filePath)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            <PictureAsPdfIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                ))}
                                                {(req.reportFiles || []).length === 0 && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        No PDFs
                                                    </Typography>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default PatientDetail;
